'use client';

import cn from 'classnames';
import { ButtonHTMLAttributes, useState } from 'react';
import { CgSpinner } from 'react-icons/cg';

import ArticleTileLarge from '@components/Article/ArticleTileLarge';
import { fetchApi } from '@utils/api/client';
import { WRPost } from '@utils/api/server';


interface PremiumLoadMoreProps {
  excludeIds?: number[];
  buttonLabel: string;
}

export default function PremiumLoadMore({
  excludeIds = [],
  buttonLabel,
}: PremiumLoadMoreProps) {
  const [posts, setPosts] = useState<WRPost[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchArticles = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetchApi<{
        canFetchMore: boolean;
        articles: WRPost[];
      }>('/wall/premium/more', {
        params: {
          excludeIds,
          page,
        },
      });

      if (response.status !== 200) {
        setHasMore(false);
        return;
      }

      if (!response.data.canFetchMore) {
        setHasMore(false);
      }

      setPosts((prevState) => [...prevState, ...response.data.articles]);
      setPage((_page) => _page + 1);
      setIsLoading(false);
    } catch (e) {
      setHasMore(false);
    }
  };

  return (
    <div className="grid gap-2 grid-cols-2 lg:gap-3 ">
      {posts.map((post, index) => (
        <ArticleTileLarge className='col-span-2 lg:col-span-1' key={index} article={post} />
      ))}

      {hasMore && (
        <PremiumLoadMoreButton
          disabled={isLoading}
          loading={isLoading}
          className="col-span-2"
          onClick={fetchArticles}
        >
          {buttonLabel}
        </PremiumLoadMoreButton>
      )}
    </div>
  );
}

interface PremiumLoadMoreButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
}

const PremiumLoadMoreButton = ({
  className,
  loading,
  children,
  ...props
}: PremiumLoadMoreButtonProps) => {
  return (
    <button
      {...props}
      className={cn(
        'flex items-center justify-center rounded-2xl bg-orange-500 px-6 py-4 text-lg font-bold text-white transition-colors hover:bg-orange-700 disabled:opacity-60',
        className,
      )}
    >
      {loading && <CgSpinner className="mr-2 h-6 w-6 animate-spin" />}
      {children}
    </button>
  );
};
